import { useZinsserStore } from '@/stores/useZinsserStore'
import { marked } from 'marked'
import { useMemo } from 'react'

// Configure marked to allow HTML passthrough
marked.use({
  breaks: true,
  gfm: true,
})

/**
 * Extract markdown prefix (list marker, header) from content.
 * Returns [prefix, rest] where prefix should stay outside the annotation span.
 */
function extractMarkdownPrefix(content: string): [string, string] {
  // Match leading whitespace + list markers (- * + or 1.) or headers (#)
  const match = content.match(/^([\s]*(?:[-*+]|\d+\.)[\s]+|[\s]*#{1,6}[\s]+)/)
  if (match) {
    return [match[1], content.slice(match[1].length)]
  }
  return ['', content]
}

export function ZinsserAIChangesPanel() {
  const { zinsserDocAnnotated } = useZinsserStore()

  // Convert annotated markdown to HTML, preserving <ins>/<del> tags
  const annotatedHtml = useMemo(() => {
    if (!zinsserDocAnnotated) return ''

    // Strategy: Move markdown prefixes OUTSIDE the annotation spans
    // So "- <ins>text</ins>" parses as a proper list item
    let content = zinsserDocAnnotated

    // For insertions: move markdown prefix outside the span
    content = content.replace(/<ins>([\s\S]*?)<\/ins>/g, (_match, p1) => {
      const [prefix, rest] = extractMarkdownPrefix(p1)
      if (prefix && rest) {
        return `${prefix}<span class="annotation-addition">${rest}</span>`
      }
      return `<span class="annotation-addition">${p1}</span>`
    })

    // For deletions: move markdown prefix outside the span
    content = content.replace(/<del>([\s\S]*?)<\/del>/g, (_match, p1) => {
      const [prefix, rest] = extractMarkdownPrefix(p1)
      if (prefix && rest) {
        return `${prefix}<span class="annotation-deletion">${rest}</span>`
      }
      return `<span class="annotation-deletion">${p1}</span>`
    })

    // Parse markdown - marked preserves HTML tags by default
    const html = marked.parse(content, { async: false }) as string

    return html
  }, [zinsserDocAnnotated])

  if (!zinsserDocAnnotated) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
        No changes were made
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto px-4 pt-3 pb-20 md:pb-[30vh]">
      <div
        className="prose-zinsser min-h-full"
        dangerouslySetInnerHTML={{ __html: annotatedHtml }}
      />
      <style>{`
        .prose-zinsser .annotation-addition {
          color: rgb(22, 163, 74) !important;
          background: none !important;
          border-bottom: none !important;
          padding: 0 !important;
        }
        .prose-zinsser .annotation-deletion {
          color: rgb(220, 38, 38) !important;
          text-decoration: line-through;
          background: none !important;
          border-bottom: none !important;
          padding: 0 !important;
        }
        /* Match Canvas list spacing - marked creates loose lists with <p> inside <li> */
        .prose-zinsser li {
          margin-bottom: 0.5rem;
        }
        .prose-zinsser li:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  )
}
